import {
  Box3,
  BoxGeometry,
  type BufferGeometry,
  DynamicDrawUsage,
  Group,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  Vector3
} from "three";
import type { DxfBinRackCellPlacement } from "../cad/pallet-rack-layout";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { disposeObject3DResources as disposeMeshes } from "../models/shared-model-resources";
import {
  convertLegacyLayerSpansToClearHeights,
  createRackLayerMetrics,
  type RackLayerHeightMode
} from "./rack-layer-metrics";
import {
  createCargoCellHighlight,
  createCargoCellSelectionHighlight,
  createRackSelectionHighlight
} from "./rack-shelf";

export type BinRackCargoCell = {
  cellId: string;
  cellIndex: number;
  heightIndex: number;
};

export type BinRackCargoPlacement = BinRackCargoCell & {
  slotIndex: number;
};

export type BinRackCellConfiguration = {
  cargoCapacity: number;
  cargoRowCount: number;
  length: number;
  width: number;
};

export type BinRackConfiguration = {
  layerHeights: number[];
};

type BinRackPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type BinRackOptions = {
  bottomBraceY?: number;
  cells?: DxfBinRackCellPlacement[];
  height?: number;
  heightCellCount?: number;
  layerHeightMode?: RackLayerHeightMode;
  layerHeights?: number[];
  length?: number;
  lengthCellCount?: number;
  position?: BinRackPosition;
  rotationY?: number;
  topBraceInset?: number;
  width?: number;
};

type AlignedPart = {
  content: Object3D;
  object: Group;
  size: Vector3;
};

type BinRackObjectCargo = BinRackCargoPlacement & {
  disposeOnRemove: boolean;
  kind: "object";
  root: Group;
};

type BinRackInstancedCargo = BinRackCargoPlacement & {
  instanceIndex: number;
  kind: "instance";
};

type BinRackCargo = BinRackInstancedCargo | BinRackObjectCargo;

export type BinRackSetCargoOptions = {
  disposeOnRemove?: boolean;
};

export type BinRackFillCargoOptions = {
  maxPrimitiveTriangles?: number;
};

type BinRackCargoBatchPrimitive = {
  instanceMesh: InstancedMesh;
  normalizedMatrix: Matrix4;
};

type BinRackCargoBatch = {
  ownedGeometries: BufferGeometry[];
  primitives: BinRackCargoBatchPrimitive[];
  root: Group;
};

type BinRackCellFrame = {
  backZ: number;
  cell: DxfBinRackCellPlacement;
  frontZ: number;
  leftX: number;
  rightX: number;
};

type BinRackHitTarget = Mesh<BoxGeometry, MeshBasicMaterial> & {
  userData: {
    binRack: BinRack;
    binRackCargoCell: BinRackCargoCell;
  };
};

type BinRackSideFrame = {
  backZ: number;
  frontZ: number;
  x: number;
};

type UniformBinRackLayout = {
  length: number;
  lengthCellCount: number;
  width: number;
};

const defaultBinRackOptions = {
  bottomBraceY: 0.5,
  height: 4,
  heightCellCount: 4,
  length: 5.4,
  lengthCellCount: 3,
  rotationY: 0,
  topBraceInset: 0.5,
  width: 0.87
};
const defaultLayerHeight = 0.66;
const defaultLayerCount = 10;
const diagonalBraceMaxAngle = Math.PI / 3;
const diagonalBraceMinAngle = (36 * Math.PI) / 180;
const frontPostRotationY = Math.PI;
const backPostRotationY = 0;
const binCargoRotationY = -Math.PI / 2;
const rackSelectionHighlightPadding = 0.06;
const layoutTolerance = 1e-5;

export const defaultBinRackLayerHeights = Array.from(
  { length: defaultLayerCount },
  () => defaultLayerHeight
);

export class BinRack extends Group {
  private readonly bottomBraceY: number;
  private readonly cargoCellGroup = new Group();
  private readonly cargoCellHighlight = createCargoCellHighlight();
  private readonly cargoCellSelectionHighlight = createCargoCellSelectionHighlight();
  private readonly cargoGroup = new Group();
  private readonly cargoInstanceIndices = new Map<string, number>();
  private readonly cargoPlacements = new Map<string, BinRackCargo>();
  private cargoBatch?: BinRackCargoBatch;
  private cellFrames = new Map<string, BinRackCellFrame>();
  private cells: DxfBinRackCellPlacement[];
  private crossbarHeight = 0;
  private hitTargets: BinRackHitTarget[] = [];
  private layerHeightMode: RackLayerHeightMode;
  private layerHeights: number[];
  private readonly rackSelectionHighlight = createRackSelectionHighlight();
  private selected = false;
  private structureGroup = new Group();
  private readonly topBraceInset: number;
  private readonly uniformLayout?: UniformBinRackLayout;
  private updateQueue: Promise<void> = Promise.resolve();

  constructor({
    bottomBraceY = defaultBinRackOptions.bottomBraceY,
    cells,
    height = defaultBinRackOptions.height,
    heightCellCount = defaultBinRackOptions.heightCellCount,
    layerHeightMode = "clear",
    layerHeights,
    length = defaultBinRackOptions.length,
    lengthCellCount = defaultBinRackOptions.lengthCellCount,
    position = {},
    rotationY = defaultBinRackOptions.rotationY,
    topBraceInset = defaultBinRackOptions.topBraceInset,
    width = defaultBinRackOptions.width
  }: BinRackOptions = {}) {
    super();
    this.bottomBraceY = validateNonNegativeNumber(bottomBraceY, "bottomBraceY");
    this.topBraceInset = validateNonNegativeNumber(topBraceInset, "topBraceInset");
    this.layerHeightMode = layerHeightMode;

    if (cells) {
      this.cells = validateCells(cells).map((cell) => ({ ...cell }));
      this.layerHeights = validateLayerHeights(layerHeights ?? defaultBinRackLayerHeights);
    } else {
      const validHeight = validatePositiveNumber(height, "height");
      const validHeightCellCount = validatePositiveInteger(heightCellCount, "heightCellCount");
      this.cells = [];
      this.layerHeights = validateLayerHeights(
        layerHeights ??
          Array.from({ length: validHeightCellCount }, () => validHeight / validHeightCellCount)
      );
      this.uniformLayout = {
        length: validatePositiveNumber(length, "length"),
        lengthCellCount: validatePositiveInteger(lengthCellCount, "lengthCellCount"),
        width: validatePositiveNumber(width, "width")
      };
    }

    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.name = "bin-rack";
    this.structureGroup.name = "bin-rack-structure";
    this.cargoGroup.name = "bin-rack-cargo";
    this.cargoCellGroup.name = "bin-rack-cargo-cells";
    this.cargoCellGroup.add(this.cargoCellSelectionHighlight, this.cargoCellHighlight);
    this.add(
      this.structureGroup,
      this.cargoGroup,
      this.cargoCellGroup,
      this.rackSelectionHighlight
    );
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
  }

  static async create(options: BinRackOptions = {}, loader: ModelAssetLoader = modelAssetLibrary) {
    const rack = new BinRack(options);
    await rack.rebuildStructure(loader);
    return rack;
  }

  getCargoCellHitTargets() {
    return this.hitTargets;
  }

  getCargoCell(hitTarget: Object3D): BinRackCargoCell | undefined {
    if (!this.isHitTarget(hitTarget)) {
      return;
    }
    return { ...hitTarget.userData.binRackCargoCell };
  }

  getCargoCellHitTarget(cell: BinRackCargoCell) {
    return this.hitTargets.find((target) => {
      const targetCell = target.userData.binRackCargoCell;
      return targetCell.cellId === cell.cellId && targetCell.heightIndex === cell.heightIndex;
    });
  }

  getCellConfiguration(cellId: string): BinRackCellConfiguration {
    const cell = this.getCell(cellId);
    return {
      cargoCapacity: cell.cargoCapacity,
      cargoRowCount: cell.cargoRowCount,
      length: cell.length,
      width: cell.width
    };
  }

  getCellCount() {
    return this.cells.length;
  }

  getConfiguration(): BinRackConfiguration {
    return { layerHeights: [...this.layerHeights] };
  }

  showCargoCellHighlight(hitTarget: Object3D) {
    if (!this.isHitTarget(hitTarget)) {
      return;
    }

    this.cargoCellHighlight.position.copy(hitTarget.position);
    this.cargoCellHighlight.scale.copy(hitTarget.scale);
    this.cargoCellHighlight.visible = true;
  }

  hideCargoCellHighlight() {
    this.cargoCellHighlight.visible = false;
  }

  selectCargoCell(hitTarget: Object3D) {
    if (!this.isHitTarget(hitTarget)) {
      return;
    }

    this.cargoCellSelectionHighlight.position.copy(hitTarget.position);
    this.cargoCellSelectionHighlight.scale.copy(hitTarget.scale).multiplyScalar(0.94);
    this.cargoCellSelectionHighlight.visible = true;
  }

  clearCargoCellSelection() {
    this.cargoCellSelectionHighlight.visible = false;
  }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.rackSelectionHighlight.visible = selected;
    if (selected) {
      this.updateRackSelectionHighlight();
    } else {
      this.clearCargoCellSelection();
    }
  }

  setCargo(
    placement: BinRackCargoPlacement,
    object: Object3D,
    { disposeOnRemove = true }: BinRackSetCargoOptions = {}
  ) {
    const validPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(validPlacement);
    this.removeCargo(validPlacement);

    const root = createCargoRoot(object);
    const cargo: BinRackObjectCargo = {
      ...validPlacement,
      disposeOnRemove,
      kind: "object",
      root
    };
    this.cargoPlacements.set(key, cargo);
    this.cargoGroup.add(root);
    this.updateCargoPlacement(cargo);
  }

  restoreInstancedCargo(placement: BinRackCargoPlacement) {
    const validPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(validPlacement);
    const instanceIndex = this.cargoInstanceIndices.get(key);
    if (instanceIndex === undefined || !this.cargoBatch) {
      return false;
    }

    this.removeCargo(validPlacement);
    const cargo: BinRackInstancedCargo = {
      ...validPlacement,
      instanceIndex,
      kind: "instance"
    };
    this.cargoPlacements.set(key, cargo);
    this.updateCargoPlacement(cargo);
    return true;
  }

  fillCargo(object: Object3D, options: BinRackFillCargoOptions = {}) {
    this.clearCargo();
    const placementCount = this.cells.reduce(
      (sum, cell) => sum + cell.cargoCapacity * this.layerHeights.length,
      0
    );
    const cargoBatch = createCargoBatch(object, placementCount, options);
    this.cargoBatch = cargoBatch;
    this.cargoGroup.add(cargoBatch.root);
    let instanceIndex = 0;

    this.cells.forEach((cell, cellIndex) => {
      this.layerHeights.forEach((_, heightIndex) => {
        for (let slotIndex = 0; slotIndex < cell.cargoCapacity; slotIndex += 1) {
          const placement = { cellId: cell.id, cellIndex, heightIndex, slotIndex };
          const cargo: BinRackInstancedCargo = {
            ...placement,
            instanceIndex,
            kind: "instance"
          };
          const key = createCargoPlacementKey(placement);
          this.cargoInstanceIndices.set(key, instanceIndex);
          this.cargoPlacements.set(key, cargo);
          this.updateCargoPlacement(cargo);
          instanceIndex += 1;
        }
      });
    });
    updateCargoBatchBounds(cargoBatch);
  }

  removeCargo(placement: BinRackCargoPlacement) {
    const validPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(validPlacement);
    const cargo = this.cargoPlacements.get(key);
    if (!cargo) {
      return;
    }

    this.cargoPlacements.delete(key);
    if (cargo.kind === "instance") {
      this.hideCargoInstance(cargo.instanceIndex);
    } else {
      this.cargoGroup.remove(cargo.root);
      disposeCargo(cargo);
    }
  }

  setCargoCapacity(cellId: string, cargoCapacity: number) {
    const cell = this.getCell(cellId);
    const nextCargoCapacity = validatePositiveInteger(cargoCapacity, "cargoCapacity");
    if (cell.cargoCapacity === nextCargoCapacity) {
      return;
    }
    cell.cargoCapacity = nextCargoCapacity;
    this.pruneCargoPlacements();
    this.updateCargoPlacements();
  }

  setLayerHeights(layerHeights: number[], loader: ModelAssetLoader = modelAssetLibrary) {
    const nextLayerHeights = validateLayerHeights(layerHeights);
    if (areEqualNumbers(this.layerHeights, nextLayerHeights)) {
      return Promise.resolve();
    }
    return this.enqueueUpdate(async () => {
      this.layerHeightMode = "clear";
      this.layerHeights = nextLayerHeights;
      this.pruneCargoPlacements();
      await this.rebuildStructure(loader);
      this.updateCargoPlacements();
    });
  }

  private enqueueUpdate(update: () => Promise<void>) {
    const nextUpdate = this.updateQueue.then(update);
    this.updateQueue = nextUpdate.catch(() => undefined);
    return nextUpdate;
  }

  private async rebuildStructure(loader: ModelAssetLoader) {
    const previousStructure = this.structureGroup;
    const structure = new Group();
    structure.name = "bin-rack-structure";

    this.crossbarHeight = await measureBinRackCrossbarHeight(loader);
    if (this.layerHeightMode === "legacy-span") {
      this.layerHeights = validateLayerHeights(
        convertLegacyLayerSpansToClearHeights(this.layerHeights, this.crossbarHeight)
      );
      this.layerHeightMode = "clear";
    }

    const probePost = await loadAlignedPart(
      loader,
      ModelAsset.BinRackPost,
      "bin-rack-post-0",
      "bottom"
    );
    const postWidth = probePost.size.x;
    const postDepth = probePost.size.z;
    if (this.cells.length === 0 && this.uniformLayout) {
      this.cells = createUniformCells(this.uniformLayout, postWidth, postDepth);
    }
    this.cellFrames = createCellFrames(this.cells, postWidth, postDepth);

    const postPlacements = createPostPlacements(this.cellFrames);
    const firstPostPlacement = postPlacements.shift();
    const posts: AlignedPart[] = [];
    if (firstPostPlacement) {
      updatePost(probePost, firstPostPlacement, this.getTotalHeight());
      posts.push(probePost);
    } else {
      disposeMeshes(probePost.object);
    }
    const remainingPosts = await Promise.all(
      postPlacements.map((_, index) =>
        loadAlignedPart(loader, ModelAsset.BinRackPost, `bin-rack-post-${index + 1}`, "bottom")
      )
    );
    remainingPosts.forEach((post, index) => {
      const placement = postPlacements[index];
      if (placement) {
        updatePost(post, placement, this.getTotalHeight());
      }
    });
    posts.push(...remainingPosts);

    const layerYPositions = this.getInternalLayerYPositions();
    const crossbars = await this.createCrossbars(loader, layerYPositions, postWidth, postDepth);
    const panels = await this.createPanels(
      loader,
      layerYPositions,
      postWidth,
      postDepth,
      this.crossbarHeight
    );
    const braces = await this.createBraces(loader, postDepth);

    structure.add(
      ...posts.map(({ object }) => object),
      ...crossbars.map(({ object }) => object),
      ...braces,
      ...panels.map(({ object }) => object)
    );
    previousStructure.removeFromParent();
    disposeMeshes(previousStructure);
    this.structureGroup = structure;
    this.add(structure);
    this.reloadHitTargets();
    this.updateRackSelectionHighlight();
  }

  private async createCrossbars(
    loader: ModelAssetLoader,
    layerYPositions: number[],
    postWidth: number,
    postDepth: number
  ) {
    const cellFrames = [...this.cellFrames.values()];
    const placements = layerYPositions.flatMap((y, heightIndex) =>
      cellFrames.flatMap((frame, cellIndex) =>
        [false, true].map((isFront) => ({ cellIndex, frame, heightIndex, isFront, y }))
      )
    );
    const crossbars = await Promise.all(
      placements.map(({ cellIndex, heightIndex, isFront }, index) =>
        loadAlignedPart(
          loader,
          ModelAsset.BinRackCrossbar,
          `bin-rack-crossbar-${heightIndex}-${cellIndex}-${isFront ? "front" : "back"}-${index}`,
          "center"
        )
      )
    );

    crossbars.forEach(({ object, size }, index) => {
      const placement = placements[index];
      if (!placement) {
        return;
      }
      const clearLength = placement.frame.rightX - placement.frame.leftX - postWidth;
      validatePositiveNumber(clearLength, "bin rack cell clear length");
      object.position.set(
        (placement.frame.leftX + placement.frame.rightX) / 2,
        placement.y,
        placement.isFront
          ? placement.frame.backZ + (postDepth - size.z) / 2
          : placement.frame.frontZ + (size.z - postDepth) / 2
      );
      object.rotation.y = placement.isFront ? 0 : Math.PI;
      object.scale.x = clearLength / size.x;
    });
    return crossbars;
  }

  private async createPanels(
    loader: ModelAssetLoader,
    layerYPositions: number[],
    postWidth: number,
    postDepth: number,
    crossbarHeight: number
  ) {
    const cellFrames = [...this.cellFrames.values()];
    const placements = layerYPositions.flatMap((y, heightIndex) =>
      cellFrames.map((frame, cellIndex) => ({ cellIndex, frame, heightIndex, y }))
    );
    const panels = await Promise.all(
      placements.map(({ cellIndex, heightIndex }) =>
        loadAlignedPart(
          loader,
          ModelAsset.BinRackPanel,
          `bin-rack-panel-${heightIndex + 1}-${cellIndex}`,
          "center"
        )
      )
    );

    panels.forEach(({ object, size }, index) => {
      const placement = placements[index];
      if (!placement) {
        return;
      }
      const clearLength = placement.frame.rightX - placement.frame.leftX - postWidth;
      const clearWidth = placement.frame.backZ - placement.frame.frontZ - postDepth;
      validatePositiveNumber(clearLength, "bin rack panel length");
      validatePositiveNumber(clearWidth, "bin rack panel width");
      object.position.set(
        (placement.frame.leftX + placement.frame.rightX) / 2,
        placement.y + crossbarHeight / 2 - size.y / 2,
        (placement.frame.frontZ + placement.frame.backZ) / 2
      );
      object.scale.set(clearLength / size.x, 1, clearWidth / size.z);
    });
    return panels;
  }

  private async createBraces(loader: ModelAssetLoader, postDepth: number) {
    const topBraceY = this.getTotalHeight() - this.topBraceInset;
    const sideFrames = createSideFrames(this.cellFrames);
    const braces = await Promise.all(
      sideFrames.map(async (frame, frameIndex) => {
        const frontZ = frame.frontZ + postDepth / 2;
        const backZ = frame.backZ - postDepth / 2;
        const clearWidth = backZ - frontZ;
        const layout = createDiagonalBraceLayout(this.bottomBraceY, topBraceY, clearWidth);
        const parts = await Promise.all(
          Array.from({ length: 2 + (layout?.count ?? 0) }, (_, braceIndex) =>
            loadAlignedPart(
              loader,
              ModelAsset.BinRackBrace,
              `bin-rack-brace-${frameIndex}-${braceIndex}`,
              "center"
            )
          )
        );
        const [bottomBrace, topBrace, ...diagonalBraces] = parts;

        if (bottomBrace) {
          updateHorizontalBrace(
            bottomBrace,
            frame.x,
            this.bottomBraceY,
            (frontZ + backZ) / 2,
            clearWidth
          );
        }
        if (topBrace) {
          updateHorizontalBrace(topBrace, frame.x, topBraceY, (frontZ + backZ) / 2, clearWidth);
        }

        diagonalBraces.forEach((brace, braceIndex) => {
          if (!layout) {
            return;
          }
          const isForward = braceIndex % 2 === 0;
          const startY = this.bottomBraceY + layout.segmentHeight * braceIndex;
          const endY = startY + layout.segmentHeight;
          const startZ = isForward ? frontZ : backZ;
          const endZ = isForward ? backZ : frontZ;
          brace.object.position.set(frame.x, (startY + endY) / 2, (startZ + endZ) / 2);
          brace.object.rotation.x = isForward ? -layout.angle : Math.PI + layout.angle;
          brace.object.scale.z = layout.diagonalLength / brace.size.z;
        });

        return parts.map(({ object }) => object);
      })
    );
    return braces.flat();
  }

  private reloadHitTargets() {
    this.hideCargoCellHighlight();
    this.clearCargoCellSelection();
    this.hitTargets.forEach((target) => {
      this.cargoCellGroup.remove(target);
      disposeMeshes(target);
    });
    this.hitTargets = [];

    this.cells.forEach((cell, cellIndex) => {
      const frame = this.cellFrames.get(cell.id);
      if (!frame) {
        return;
      }
      this.layerHeights.forEach((height, heightIndex) => {
        const target = createHitTarget(this, { cellId: cell.id, cellIndex, heightIndex });
        const layerBottom = this.getLayerClearBottom(heightIndex);
        target.position.set(
          (frame.leftX + frame.rightX) / 2,
          layerBottom + height / 2,
          (frame.frontZ + frame.backZ) / 2
        );
        target.scale.set(frame.rightX - frame.leftX, height, frame.backZ - frame.frontZ);
        this.hitTargets.push(target);
      });
    });
    this.cargoCellGroup.add(...this.hitTargets);
  }

  private updateRackSelectionHighlight() {
    if (this.cells.length === 0) {
      this.rackSelectionHighlight.visible = false;
      return;
    }
    const bounds = getRackBounds(this.cells);
    const totalHeight = this.getTotalHeight();
    this.rackSelectionHighlight.position.set(
      (bounds.minX + bounds.maxX) / 2,
      totalHeight / 2,
      (bounds.minZ + bounds.maxZ) / 2
    );
    this.rackSelectionHighlight.scale.set(
      bounds.maxX - bounds.minX + rackSelectionHighlightPadding * 2,
      totalHeight + rackSelectionHighlightPadding * 2,
      bounds.maxZ - bounds.minZ + rackSelectionHighlightPadding * 2
    );
    this.rackSelectionHighlight.visible = this.selected;
  }

  private updateCargoPlacements() {
    this.cargoPlacements.forEach((cargo) => {
      this.updateCargoPlacement(cargo);
    });
    if (this.cargoBatch) {
      updateCargoBatchBounds(this.cargoBatch);
    }
  }

  private updateCargoPlacement(cargo: BinRackCargo) {
    const frame = this.cellFrames.get(cargo.cellId);
    const cell = this.getCell(cargo.cellId);
    if (!frame) {
      return;
    }

    const rowCount = Math.min(cell.cargoRowCount, cell.cargoCapacity);
    const columnCount = Math.ceil(cell.cargoCapacity / rowCount);
    const rowIndex = Math.floor(cargo.slotIndex / columnCount);
    const columnIndex = cargo.slotIndex % columnCount;
    const currentRowColumnCount = Math.min(
      columnCount,
      cell.cargoCapacity - rowIndex * columnCount
    );
    const xRatio = (columnIndex + 0.5) / currentRowColumnCount;
    const zRatio = (rowIndex + 0.5) / rowCount;
    const supportSurfaceOffset = cargo.heightIndex > 0 ? this.crossbarHeight / 2 : 0;
    const position = new Vector3(
      frame.leftX + (frame.rightX - frame.leftX) * xRatio,
      this.getCargoLayerBase(cargo.heightIndex) + supportSurfaceOffset,
      frame.frontZ + (frame.backZ - frame.frontZ) * zRatio
    );

    if (cargo.kind === "instance") {
      this.setCargoInstancePosition(cargo.instanceIndex, position);
    } else {
      cargo.root.position.copy(position);
    }
  }

  private pruneCargoPlacements() {
    this.cargoPlacements.forEach((cargo, key) => {
      const cell = this.cells.find((item) => item.id === cargo.cellId);
      if (
        !cell ||
        cargo.heightIndex >= this.layerHeights.length ||
        cargo.slotIndex >= cell.cargoCapacity
      ) {
        this.cargoPlacements.delete(key);
        if (cargo.kind === "instance") {
          this.hideCargoInstance(cargo.instanceIndex);
        } else {
          this.cargoGroup.remove(cargo.root);
          disposeCargo(cargo);
        }
      }
    });
  }

  private clearCargo() {
    this.cargoPlacements.forEach((cargo) => {
      if (cargo.kind === "object") {
        this.cargoGroup.remove(cargo.root);
        disposeCargo(cargo);
      }
    });
    this.cargoPlacements.clear();
    this.cargoInstanceIndices.clear();

    if (this.cargoBatch) {
      this.cargoGroup.remove(this.cargoBatch.root);
      disposeCargoBatch(this.cargoBatch);
      this.cargoBatch = undefined;
    }
  }

  private hideCargoInstance(instanceIndex: number) {
    const batch = this.cargoBatch;
    if (!batch) {
      return;
    }
    const hiddenMatrix = new Matrix4().makeScale(0, 0, 0);
    batch.primitives.forEach(({ instanceMesh }) => {
      instanceMesh.setMatrixAt(instanceIndex, hiddenMatrix);
      instanceMesh.instanceMatrix.needsUpdate = true;
    });
  }

  private setCargoInstancePosition(instanceIndex: number, position: Vector3) {
    const batch = this.cargoBatch;
    if (!batch) {
      return;
    }
    const placementMatrix = new Matrix4().makeTranslation(position.x, position.y, position.z);
    batch.primitives.forEach(({ instanceMesh, normalizedMatrix }) => {
      instanceMesh.setMatrixAt(
        instanceIndex,
        new Matrix4().multiplyMatrices(placementMatrix, normalizedMatrix)
      );
      instanceMesh.instanceMatrix.needsUpdate = true;
    });
  }

  private getLayerClearBottom(heightIndex: number) {
    return this.getLayerMetrics().layers[heightIndex]?.clearBottomY ?? 0;
  }

  private getCargoLayerBase(heightIndex: number) {
    return this.getLayerMetrics().layers[heightIndex]?.cargoBaseY ?? 0;
  }

  private getInternalLayerYPositions() {
    return this.getLayerMetrics().layers.flatMap(({ crossbarCenterY }) =>
      crossbarCenterY === undefined ? [] : [crossbarCenterY]
    );
  }

  private getTotalHeight() {
    return this.getLayerMetrics().totalHeight;
  }

  private getLayerMetrics() {
    return createRackLayerMetrics(this.layerHeights, this.crossbarHeight);
  }

  private getCell(cellId: string) {
    const cell = this.cells.find((item) => item.id === cellId);
    if (!cell) {
      throw new RangeError(`Unknown bin rack cell: ${cellId}`);
    }
    return cell;
  }

  private validateCargoPlacement(placement: BinRackCargoPlacement): BinRackCargoPlacement {
    const cell = this.getCell(placement.cellId);
    validateIndex(placement.cellIndex, this.cells.length, "cellIndex");
    if (this.cells[placement.cellIndex]?.id !== placement.cellId) {
      throw new RangeError("cellIndex does not match cellId");
    }
    validateIndex(placement.heightIndex, this.layerHeights.length, "heightIndex");
    validateIndex(placement.slotIndex, cell.cargoCapacity, "slotIndex");
    return { ...placement };
  }

  private isHitTarget(object: Object3D): object is BinRackHitTarget {
    return object.userData.binRack === this && object.userData.binRackCargoCell !== undefined;
  }
}

async function measureBinRackCrossbarHeight(loader: ModelAssetLoader) {
  const crossbar = await loadAlignedPart(
    loader,
    ModelAsset.BinRackCrossbar,
    "bin-rack-crossbar-probe",
    "center"
  );
  const height = crossbar.size.y;
  disposeMeshes(crossbar.object);
  return height;
}

async function loadAlignedPart(
  loader: ModelAssetLoader,
  asset: ModelAsset,
  name: string,
  origin: "bottom" | "center"
): Promise<AlignedPart> {
  const content = await loader.load(asset);
  content.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(content);
  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const object = new Group();
  const contentRoot = new Group();

  content.position.x -= center.x;
  content.position.y -= origin === "bottom" ? bounds.min.y : center.y;
  content.position.z -= center.z;
  object.name = name;
  contentRoot.add(content);
  object.add(contentRoot);
  return { content: contentRoot, object, size };
}

function createUniformCells(
  layout: UniformBinRackLayout,
  postWidth: number,
  postDepth: number
): DxfBinRackCellPlacement[] {
  const cellLength = layout.length / layout.lengthCellCount;
  return Array.from({ length: layout.lengthCellCount }, (_, index) => ({
    cargoCapacity: 1,
    cargoRowCount: 1,
    id: `uniform-bin-rack-cell-${index}`,
    length: cellLength + postWidth,
    positionX: cellLength * index - postWidth / 2,
    positionZ: -postDepth / 2,
    width: layout.width + postDepth
  }));
}

function createCellFrames(cells: DxfBinRackCellPlacement[], postWidth: number, postDepth: number) {
  const frames = new Map<string, BinRackCellFrame>();
  cells.forEach((cell, index) => {
    const previous = cells[index - 1];
    const next = cells[index + 1];
    const previousOverlap = previous
      ? Math.max(
          0,
          Math.min(previous.positionX + previous.length, cell.positionX + cell.length) -
            Math.max(previous.positionX, cell.positionX)
        )
      : 0;
    const nextOverlap = next
      ? Math.max(
          0,
          Math.min(next.positionX + next.length, cell.positionX + cell.length) -
            Math.max(next.positionX, cell.positionX)
        )
      : 0;
    const leftInset =
      previousOverlap > layoutTolerance
        ? previousOverlap / 2
        : Math.max(nextOverlap / 2, postWidth / 2);
    const rightInset =
      nextOverlap > layoutTolerance
        ? nextOverlap / 2
        : Math.max(previousOverlap / 2, postWidth / 2);
    const frontInset = Math.min(postDepth / 2, cell.width / 4);
    frames.set(cell.id, {
      backZ: cell.positionZ + cell.width - frontInset,
      cell,
      frontZ: cell.positionZ + frontInset,
      leftX: cell.positionX + leftInset,
      rightX: cell.positionX + cell.length - rightInset
    });
  });
  return frames;
}

function createPostPlacements(frames: Map<string, BinRackCellFrame>) {
  const placements = new Map<string, { isFront: boolean; x: number; z: number }>();
  frames.forEach((frame) => {
    [frame.leftX, frame.rightX].forEach((x) => {
      [
        { isFront: false, z: frame.frontZ },
        { isFront: true, z: frame.backZ }
      ].forEach((placement) => {
        const key = `${roundLayoutValue(x)}:${roundLayoutValue(placement.z)}`;
        if (!placements.has(key)) {
          placements.set(key, { ...placement, x });
        }
      });
    });
  });
  return [...placements.values()];
}

function updatePost(
  post: AlignedPart,
  placement: { isFront: boolean; x: number; z: number },
  height: number
) {
  post.object.position.set(placement.x, 0, placement.z);
  post.object.scale.y = height / post.size.y;
  post.content.rotation.y = placement.isFront ? frontPostRotationY : backPostRotationY;
}

function createSideFrames(frames: Map<string, BinRackCellFrame>) {
  const sideFrames = new Map<string, BinRackSideFrame>();
  frames.forEach((frame) => {
    [frame.leftX, frame.rightX].forEach((x) => {
      const key = `${roundLayoutValue(x)}:${roundLayoutValue(frame.frontZ)}:${roundLayoutValue(frame.backZ)}`;
      if (!sideFrames.has(key)) {
        sideFrames.set(key, { backZ: frame.backZ, frontZ: frame.frontZ, x });
      }
    });
  });
  return [...sideFrames.values()];
}

function updateHorizontalBrace(
  brace: AlignedPart,
  x: number,
  y: number,
  z: number,
  targetLength: number
) {
  brace.object.position.set(x, y, z);
  brace.object.scale.z = targetLength / brace.size.z;
}

function createDiagonalBraceLayout(bottomY: number, topY: number, width: number) {
  const height = topY - bottomY;
  if (height <= 0 || width <= 0) {
    return undefined;
  }

  const minCount = Math.max(1, Math.ceil(height / (Math.tan(diagonalBraceMaxAngle) * width)));
  const maxCount = Math.floor(height / (Math.tan(diagonalBraceMinAngle) * width));
  if (maxCount < minCount) {
    return undefined;
  }

  const count = maxCount;
  const segmentHeight = height / count;
  return {
    angle: Math.atan2(segmentHeight, width),
    count,
    diagonalLength: Math.hypot(segmentHeight, width),
    segmentHeight
  };
}

function createHitTarget(rack: BinRack, cell: BinRackCargoCell) {
  const material = new MeshBasicMaterial({ depthWrite: false, opacity: 0, transparent: true });
  material.colorWrite = false;
  const target = new Mesh(new BoxGeometry(1, 1, 1), material) as BinRackHitTarget;
  target.name = `bin-rack-cell-${cell.cellIndex}-${cell.heightIndex}`;
  target.userData.binRack = rack;
  target.userData.binRackCargoCell = cell;
  target.visible = false;
  return target;
}

function getRackBounds(cells: DxfBinRackCellPlacement[]) {
  return {
    maxX: Math.max(...cells.map((cell) => cell.positionX + cell.length)),
    maxZ: Math.max(...cells.map((cell) => cell.positionZ + cell.width)),
    minX: Math.min(...cells.map((cell) => cell.positionX)),
    minZ: Math.min(...cells.map((cell) => cell.positionZ))
  };
}

function createCargoBatch(
  object: Object3D,
  placementCount: number,
  { maxPrimitiveTriangles }: BinRackFillCargoOptions
): BinRackCargoBatch {
  object.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(object);
  const center = bounds.getCenter(new Vector3());
  const normalizationMatrix = new Matrix4().makeTranslation(-center.x, -bounds.min.y, -center.z);
  const orientationMatrix = new Matrix4().makeRotationY(binCargoRotationY);
  const root = new Group();
  const primitives: BinRackCargoBatchPrimitive[] = [];
  const ownedGeometries: BufferGeometry[] = [];
  root.name = "bin-rack-cargo-instances";

  object.traverse((child) => {
    if (!(child instanceof Mesh) || child instanceof InstancedMesh) {
      return;
    }

    const geometry = createCargoPrimitiveGeometry(
      child.geometry,
      maxPrimitiveTriangles,
      ownedGeometries
    );
    const instanceMesh = new InstancedMesh(geometry, child.material, placementCount);
    instanceMesh.name = `bin-rack-cargo-instance-${primitives.length}`;
    instanceMesh.castShadow = child.castShadow;
    instanceMesh.receiveShadow = child.receiveShadow;
    instanceMesh.instanceMatrix.setUsage(DynamicDrawUsage);
    root.add(instanceMesh);
    primitives.push({
      instanceMesh,
      normalizedMatrix: orientationMatrix
        .clone()
        .multiply(normalizationMatrix)
        .multiply(child.matrixWorld)
    });
  });

  if (primitives.length === 0) {
    throw new Error("Bin rack cargo model does not contain any mesh primitives");
  }

  return { ownedGeometries, primitives, root };
}

function createCargoPrimitiveGeometry(
  geometry: BufferGeometry,
  maxPrimitiveTriangles: number | undefined,
  ownedGeometries: BufferGeometry[]
) {
  if (
    maxPrimitiveTriangles === undefined ||
    getGeometryTriangleCount(geometry) <= maxPrimitiveTriangles
  ) {
    return geometry;
  }

  geometry.computeBoundingBox();
  const bounds = geometry.boundingBox;
  if (!bounds || bounds.isEmpty()) {
    return geometry;
  }

  const size = bounds.getSize(new Vector3());
  const center = bounds.getCenter(new Vector3());
  const proxyGeometry = new BoxGeometry(size.x, size.y, size.z);
  proxyGeometry.translate(center.x, center.y, center.z);
  ownedGeometries.push(proxyGeometry);
  return proxyGeometry;
}

function getGeometryTriangleCount(geometry: BufferGeometry) {
  return Math.floor((geometry.index?.count ?? geometry.attributes.position?.count ?? 0) / 3);
}

function updateCargoBatchBounds(batch: BinRackCargoBatch) {
  batch.primitives.forEach(({ instanceMesh }) => {
    instanceMesh.computeBoundingBox();
    instanceMesh.computeBoundingSphere();
  });
}

function disposeCargoBatch(batch: BinRackCargoBatch) {
  batch.primitives.forEach(({ instanceMesh }) => {
    instanceMesh.dispose();
  });
  batch.ownedGeometries.forEach((geometry) => {
    geometry.dispose();
  });
}

function createCargoRoot(object: Object3D) {
  const root = new Group();
  root.rotation.y = binCargoRotationY;
  root.add(object);
  root.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(object);
  const center = bounds.getCenter(new Vector3());
  object.position.x -= center.x;
  object.position.y -= bounds.min.y;
  object.position.z -= center.z;
  return root;
}

function createCargoPlacementKey(placement: BinRackCargoPlacement) {
  return `${placement.cellId}:${placement.heightIndex}:${placement.slotIndex}`;
}

function validateCells(cells: DxfBinRackCellPlacement[]) {
  if (cells.length === 0) {
    throw new RangeError("BinRack requires at least one cell");
  }
  cells.forEach((cell) => {
    validatePositiveNumber(cell.length, "cell.length");
    validatePositiveNumber(cell.width, "cell.width");
    validatePositiveInteger(cell.cargoCapacity, "cell.cargoCapacity");
    validatePositiveInteger(cell.cargoRowCount, "cell.cargoRowCount");
    if (cell.cargoRowCount > cell.cargoCapacity) {
      throw new RangeError("cell.cargoRowCount cannot exceed cell.cargoCapacity");
    }
  });
  return cells;
}

function validateLayerHeights(layerHeights: number[]) {
  if (layerHeights.length === 0) {
    throw new RangeError("layerHeights requires at least one layer");
  }
  layerHeights.forEach((height) => {
    validatePositiveNumber(height, "layerHeight");
  });
  return [...layerHeights];
}

function validatePositiveNumber(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function validateNonNegativeNumber(value: number, name: string) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function validatePositiveInteger(value: number, name: string) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function validateIndex(value: number, count: number, name: string) {
  if (!Number.isInteger(value) || value < 0 || value >= count) {
    throw new RangeError(`${name} must be an integer between 0 and ${count - 1}`);
  }
}

function roundLayoutValue(value: number) {
  return Math.round(value / layoutTolerance) * layoutTolerance;
}

function areEqualNumbers(left: number[], right: number[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function disposeCargo(cargo: BinRackObjectCargo) {
  if (cargo.disposeOnRemove) {
    disposeMeshes(cargo.root);
  }
}
